/**
 * dashboardStore — Dashboard "Business OS" (multi-business, 2026-07-31).
 *
 * A tenant owns MANY named businesses; each holds the four onboarding answers,
 * per-step Build status (build_state), and links to one or more vibe projects.
 * The STORE OF RECORD is the server (`v3_tenant_business_profile` +
 * `v3_business_project`, via the vibe backend `/api/business-profile/businesses`).
 *
 * This store keeps the full `businesses` list for the switcher AND mirrors the
 * ACTIVE business's editable fields (`profile` / `onboardingStep` /
 * `onboardingCompleted` / `buildState`) at the top level, so the wizard + action
 * cards read the active business through the SAME selectors they always used
 * (`s.profile.data`, `s.onboardingStep`, …) with no per-component change.
 *
 * The four questions (design doc §5): business / customers / data / tasks.
 *
 * Design doc:
 * docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/services/api';
import { useProjectStore } from '@/stores/projectStore';

export interface BusinessProfile {
  /** Q1 — what is your business (offer/products/audience). */
  business: string;
  /** Q2 — how do you get customers (channels/funnel). */
  customers: string;
  /** Q3 — what data you need to run it (the ingest plan seed). */
  data: string;
  /** Q4 — what must get done (recurring tasks + one-offs). */
  tasks: string;
}

export const EMPTY_PROFILE: BusinessProfile = {
  business: '',
  customers: '',
  data: '',
  tasks: '',
};

/** The Build-Engine steps whose status is tracked per business. Card → step. */
export const BUILD_STEPS = ['data', 'workflows', 'site', 'crm', 'agents'] as const;
export type BuildStepKey = (typeof BUILD_STEPS)[number];
export type BuildStepStatus = 'todo' | 'in_progress' | 'done';
export type BuildState = Partial<Record<BuildStepKey, BuildStepStatus>>;

/** A project link on a business (project_id is a vibe project uuid). */
export interface BusinessProjectLink {
  project_id: string;
  is_primary: boolean;
}

/** One named business (server shape from GET /businesses). */
export interface Business {
  id: number;
  name: string;
  profile: BusinessProfile;
  onboardingStep: number;
  onboardingCompleted: boolean;
  buildState: BuildState;
  projects: BusinessProjectLink[];
}

/**
 * The four onboarding questions, in order. `key` maps to a BusinessProfile
 * field; the wizard renders one per step. Exported so the component + tests
 * share one source of truth (there are exactly 4 — TOTAL_STEPS).
 */
export const ONBOARDING_QUESTIONS = [
  {
    key: 'business',
    title: 'What is your business?',
    help: 'Tell us what you do — your offer, product, or service, and who it is for.',
    placeholder: 'e.g. A neighborhood bakery selling fresh bread and custom cakes.',
  },
  {
    key: 'customers',
    title: 'How do you get customers?',
    help: 'Where do customers come from — content, ads, referrals, walk-ins, a mailing list?',
    placeholder: 'e.g. Instagram posts, local foot traffic, and repeat wedding orders.',
  },
  {
    key: 'data',
    title: 'What data do you need to run your business?',
    help: 'What should we bring in — your website, documents, calendar, CRM, transcripts?',
    placeholder: 'e.g. Our menu PDF, the website, and the order spreadsheet.',
  },
  {
    key: 'tasks',
    title: 'What needs to get done?',
    help: 'The recurring tasks and one-offs that keep things running.',
    placeholder: 'e.g. Reply to order enquiries, post 3x/week, restock supplies weekly.',
  },
] as const satisfies ReadonlyArray<{
  key: keyof BusinessProfile;
  title: string;
  help: string;
  placeholder: string;
}>;

export const TOTAL_STEPS = ONBOARDING_QUESTIONS.length; // 4

/** Server shapes from the /businesses collection. */
interface BusinessResponse {
  id: number;
  name: string;
  profile: Record<string, unknown>;
  build_state: Record<string, unknown>;
  onboarding_step: number;
  onboarding_completed: boolean;
  projects: BusinessProjectLink[];
}
interface BusinessListResponse {
  tenant: string;
  businesses: BusinessResponse[];
}

/** Coerce the server's free-form profile JSON into a full BusinessProfile
 * (missing / non-string keys default to ''). Keeps the UI total-function. */
export function coerceProfile(raw: unknown): BusinessProfile {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const s = (k: keyof BusinessProfile): string => (typeof o[k] === 'string' ? (o[k] as string) : '');
  return { business: s('business'), customers: s('customers'), data: s('data'), tasks: s('tasks') };
}

/** Coerce server build_state JSON into a typed BuildState (known steps only). */
export function coerceBuildState(raw: unknown): BuildState {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out: BuildState = {};
  for (const step of BUILD_STEPS) {
    const v = o[step];
    if (v === 'todo' || v === 'in_progress' || v === 'done') out[step] = v;
  }
  return out;
}

function coerceBusiness(r: BusinessResponse): Business {
  return {
    id: r.id,
    name: r.name,
    profile: coerceProfile(r.profile),
    onboardingStep: clampStep(r.onboarding_step ?? 0),
    onboardingCompleted: !!r.onboarding_completed,
    buildState: coerceBuildState(r.build_state),
    projects: Array.isArray(r.projects) ? r.projects : [],
  };
}

function clampStep(step: number): number {
  return Math.max(0, Math.min(TOTAL_STEPS, Math.round(step)));
}

/** The primary project id of a business, or the first linked, or null. */
export function primaryProjectId(biz: Business | undefined): string | null {
  if (!biz || !biz.projects.length) return null;
  const primary = biz.projects.find((p) => p.is_primary);
  return (primary ?? biz.projects[0]).project_id;
}

interface DashboardState {
  // Collection
  businesses: Business[];
  activeBusinessId: number | null;

  // Active-business editable cache (drives the wizard + action cards).
  profile: BusinessProfile;
  onboardingStep: number;
  onboardingCompleted: boolean;
  buildState: BuildState;

  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  /** True once loadBusinesses() has run this session (server-refresh guard). */
  loaded: boolean;

  loadBusinesses: () => Promise<void>;
  createBusiness: (name: string) => Promise<Business | null>;
  setActiveBusiness: (id: number) => void;
  renameBusiness: (id: number, name: string) => Promise<void>;
  deleteBusiness: (id: number) => Promise<void>;

  setAnswer: (key: keyof BusinessProfile, value: string) => void;
  setStep: (step: number) => void;
  saveProfile: (opts?: { completed?: boolean }) => Promise<void>;

  setBuildStepStatus: (step: BuildStepKey, status: BuildStepStatus) => Promise<void>;
  /** Prepare a Build step: switch to the active business's primary project so Pi
   * builds THERE (not "whatever project is open"), then mark it in_progress. */
  startBuildStep: (step: BuildStepKey) => Promise<void>;

  reset: () => void;
}

const ACTIVE_KEY = 'vibe-designer:active-business-id';

/** Load the active-business editable cache from a Business (or empty). */
function activeCache(biz: Business | undefined) {
  return {
    profile: biz ? biz.profile : { ...EMPTY_PROFILE },
    onboardingStep: biz ? biz.onboardingStep : 0,
    onboardingCompleted: biz ? biz.onboardingCompleted : false,
    buildState: biz ? biz.buildState : {},
  };
}

export const dashboardStorePartialize = (
  state: DashboardState,
): { activeBusinessId: number | null } => ({ activeBusinessId: state.activeBusinessId });

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      businesses: [],
      activeBusinessId: null,
      profile: { ...EMPTY_PROFILE },
      onboardingStep: 0,
      onboardingCompleted: false,
      buildState: {},
      isLoading: false,
      isSaving: false,
      error: null,
      loaded: false,

      loadBusinesses: async () => {
        set({ isLoading: true, error: null });
        try {
          const r = await api.get<BusinessListResponse>('/business-profile/businesses');
          const businesses = (r.businesses ?? []).map(coerceBusiness);
          // Keep the persisted active id if it still exists; else the first.
          const persisted = get().activeBusinessId;
          const active =
            businesses.find((b) => b.id === persisted) ?? businesses[0] ?? undefined;
          set({
            businesses,
            activeBusinessId: active ? active.id : null,
            ...activeCache(active),
            isLoading: false,
            loaded: true,
          });
        } catch (e) {
          set({
            isLoading: false,
            loaded: true,
            error: e instanceof Error ? e.message : 'Failed to load your businesses',
          });
        }
      },

      createBusiness: async (name) => {
        set({ isSaving: true, error: null });
        try {
          const created = coerceBusiness(
            await api.post<BusinessResponse>('/business-profile/businesses', { name }),
          );
          // Auto-create a project for this business and link it as primary. Reuse
          // projectStore.createProject (it does all the Forgejo provisioning);
          // best-effort — a project failure must not lose the business.
          let projects: BusinessProjectLink[] = [];
          try {
            await useProjectStore.getState().createProject(name);
            const projectId = useProjectStore.getState().currentProject?.id;
            if (projectId) {
              const withProject = coerceBusiness(
                await api.post<BusinessResponse>(
                  `/business-profile/businesses/${created.id}/projects`,
                  { project_id: projectId, is_primary: true },
                ),
              );
              projects = withProject.projects;
            }
          } catch {
            // Project link failed — the business still exists (degraded, no
            // primary project yet; the user can link one later).
          }
          const full: Business = { ...created, projects };
          writeActiveId(full.id);
          set((s) => ({
            businesses: [...s.businesses, full],
            activeBusinessId: full.id,
            ...activeCache(full),
            isSaving: false,
          }));
          return full;
        } catch (e) {
          set({
            isSaving: false,
            error: e instanceof Error ? e.message : 'Failed to create the business',
          });
          return null;
        }
      },

      setActiveBusiness: (id) => {
        const biz = get().businesses.find((b) => b.id === id);
        if (!biz) return;
        writeActiveId(id);
        set({ activeBusinessId: id, error: null, ...activeCache(biz) });
      },

      renameBusiness: async (id, name) => {
        try {
          const updated = coerceBusiness(
            await api.put<BusinessResponse>(`/business-profile/businesses/${id}`, { name }),
          );
          set((s) => ({
            businesses: s.businesses.map((b) => (b.id === id ? updated : b)),
            error: null,
          }));
        } catch (e) {
          set({ error: e instanceof Error ? e.message : 'Failed to rename the business' });
        }
      },

      deleteBusiness: async (id) => {
        try {
          await api.delete(`/business-profile/businesses/${id}`);
        } catch (e) {
          set({ error: e instanceof Error ? e.message : 'Failed to delete the business' });
          return;
        }
        const remaining = get().businesses.filter((b) => b.id !== id);
        const wasActive = get().activeBusinessId === id;
        const next = wasActive ? remaining[0] : get().businesses.find((b) => b.id === get().activeBusinessId);
        if (wasActive) writeActiveId(next ? next.id : null);
        set({
          businesses: remaining,
          activeBusinessId: next ? next.id : null,
          ...(wasActive ? activeCache(next) : {}),
          error: null,
        });
      },

      setAnswer: (key, value) => set({ profile: { ...get().profile, [key]: value } }),

      setStep: (step) => set({ onboardingStep: clampStep(step) }),

      saveProfile: async (opts) => {
        const id = get().activeBusinessId;
        if (id == null) return;
        set({ isSaving: true, error: null });
        const body: Record<string, unknown> = {
          profile: get().profile,
          onboarding_step: get().onboardingStep,
          build_state: get().buildState,
        };
        if (opts?.completed !== undefined) body.completed = opts.completed;
        try {
          const updated = coerceBusiness(
            await api.put<BusinessResponse>(`/business-profile/businesses/${id}`, body),
          );
          set((s) => ({
            businesses: s.businesses.map((b) => (b.id === id ? updated : b)),
            ...activeCache(updated),
            isSaving: false,
          }));
        } catch (e) {
          set({
            isSaving: false,
            error: e instanceof Error ? e.message : 'Failed to save your answers',
          });
          throw e; // let the wizard keep the user on the current step
        }
      },

      setBuildStepStatus: async (step, status) => {
        const id = get().activeBusinessId;
        const nextState: BuildState = { ...get().buildState, [step]: status };
        set({ buildState: nextState });
        if (id == null) return;
        try {
          const updated = coerceBusiness(
            await api.put<BusinessResponse>(`/business-profile/businesses/${id}`, {
              build_state: nextState,
            }),
          );
          set((s) => ({
            businesses: s.businesses.map((b) => (b.id === id ? updated : b)),
            buildState: updated.buildState,
          }));
        } catch (e) {
          set({ error: e instanceof Error ? e.message : 'Failed to save build progress' });
        }
      },

      startBuildStep: async (step) => {
        const biz = get().businesses.find((b) => b.id === get().activeBusinessId);
        const projectId = primaryProjectId(biz);
        if (projectId) {
          // Build in the business's OWN project, not whatever is open.
          try {
            await useProjectStore.getState().switchProject(projectId);
          } catch {
            // A failed switch must not block kicking off the routine.
          }
        }
        await get().setBuildStepStatus(step, 'in_progress');
      },

      reset: () =>
        set({
          businesses: [],
          activeBusinessId: null,
          profile: { ...EMPTY_PROFILE },
          onboardingStep: 0,
          onboardingCompleted: false,
          buildState: {},
          isLoading: false,
          isSaving: false,
          error: null,
          loaded: false,
        }),
    }),
    {
      name: 'vibe-designer-dashboard',
      partialize: dashboardStorePartialize,
    },
  ),
);

/** Persist the active-business id outside zustand too (parity with the project
 * store's active-id key), tolerant of quota / privacy mode. */
function writeActiveId(id: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id === null) window.localStorage.removeItem(ACTIVE_KEY);
    else window.localStorage.setItem(ACTIVE_KEY, String(id));
  } catch {
    /* ignore */
  }
}
